import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { adminApi } from '../../services/api';
import {
  ArrowLeft,
  Plus,
  DollarSign,
  MoreVertical,
  Calendar,
  User,
  GripVertical
} from 'lucide-react';
import toast from 'react-hot-toast';

const stages = [
  { id: 'prospecting', name: 'Prospecting', color: 'bg-gray-500' },
  { id: 'qualification', name: 'Qualification', color: 'bg-blue-500' },
  { id: 'proposal', name: 'Proposal', color: 'bg-yellow-500' },
  { id: 'negotiation', name: 'Negotiation', color: 'bg-purple-500' },
  { id: 'closed_won', name: 'Closed Won', color: 'bg-green-500' },
  { id: 'closed_lost', name: 'Closed Lost', color: 'bg-red-500' }
];

const PipelinePage = () => {
  const [opportunities, setOpportunities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draggedItem, setDraggedItem] = useState(null);

  useEffect(() => {
    fetchOpportunities();
  }, []);

  const fetchOpportunities = async () => {
    setLoading(true);
    try {
      const response = await adminApi.getOpportunities({ limit: 100 });
      setOpportunities(response.data.data || []);
    } catch (err) {
      toast.error('Failed to load opportunities');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const getOpportunitiesByStage = (stageId) => {
    return opportunities.filter((opp) => opp.stage === stageId);
  };

  const getStageTotal = (stageId) => {
    return getOpportunitiesByStage(stageId).reduce((sum, opp) => sum + (opp.value || 0), 0);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value || 0);
  };

  const handleDragStart = (e, opportunity) => {
    setDraggedItem(opportunity);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e, stageId) => {
    e.preventDefault();
    if (!draggedItem || draggedItem.stage === stageId) {
      setDraggedItem(null);
      return;
    }

    try {
      await adminApi.updateOpportunity(draggedItem.id, { stage: stageId });

      setOpportunities((prev) =>
        prev.map((opp) =>
          opp.id === draggedItem.id ? { ...opp, stage: stageId } : opp
        )
      );

      toast.success(`Moved to ${stages.find(s => s.id === stageId)?.name}`);
    } catch (err) {
      toast.error('Failed to update opportunity');
      console.error(err);
    } finally {
      setDraggedItem(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          <Link
            to="/sales/opportunities"
            className="mr-4 p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
          >
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Sales Pipeline</h1>
            <p className="text-gray-500 mt-1">Drag and drop opportunities between stages</p>
          </div>
        </div>
        <Link to="/sales/opportunities" className="btn-primary">
          <Plus className="w-5 h-5 mr-2" />
          New Opportunity
        </Link>
      </div>

      {/* Pipeline Stats */}
      <div className="card p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-500">Total Pipeline Value</p>
            <p className="text-2xl font-bold text-gray-900">
              {formatCurrency(opportunities.reduce((sum, opp) => {
                if (opp.stage !== 'closed_lost') {
                  return sum + (opp.value || 0);
                }
                return sum;
              }, 0))}
            </p>
          </div>
          <div className="flex items-center gap-6">
            <div className="text-center">
              <p className="text-2xl font-bold text-green-600">
                {formatCurrency(getStageTotal('closed_won'))}
              </p>
              <p className="text-xs text-gray-500">Won</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-gray-900">{opportunities.length}</p>
              <p className="text-xs text-gray-500">Total Deals</p>
            </div>
          </div>
        </div>
      </div>

      {/* Kanban Board */}
      <div className="flex gap-4 overflow-x-auto pb-4">
        {stages.map((stage) => {
          const stageOpportunities = getOpportunitiesByStage(stage.id);
          const stageTotal = getStageTotal(stage.id);

          return (
            <div
              key={stage.id}
              className="flex-shrink-0 w-72"
              onDragOver={handleDragOver}
              onDrop={(e) => handleDrop(e, stage.id)}
            >
              <div className="bg-gray-100 rounded-lg p-3">
                {/* Stage Header */}
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center">
                    <div className={`w-3 h-3 rounded-full ${stage.color} mr-2`} />
                    <h3 className="font-medium text-gray-900">{stage.name}</h3>
                    <span className="ml-2 px-2 py-0.5 bg-gray-200 rounded-full text-xs text-gray-600">
                      {stageOpportunities.length}
                    </span>
                  </div>
                </div>

                {/* Stage Total */}
                <div className="text-sm text-gray-500 mb-3">
                  {formatCurrency(stageTotal)}
                </div>

                {/* Cards */}
                <div className="space-y-2 min-h-[200px]">
                  {stageOpportunities.map((opportunity) => (
                    <div
                      key={opportunity.id}
                      draggable
                      onDragStart={(e) => handleDragStart(e, opportunity)}
                      className={`bg-white rounded-lg p-3 shadow-sm border border-gray-200 cursor-move hover:shadow-md transition-shadow ${
                        draggedItem?.id === opportunity.id ? 'opacity-50' : ''
                      }`}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center">
                          <GripVertical className="w-4 h-4 text-gray-300 mr-1" />
                          <h4 className="font-medium text-gray-900 text-sm">
                            {opportunity.name}
                          </h4>
                        </div>
                        <button className="p-1 text-gray-400 hover:text-gray-600 rounded">
                          <MoreVertical className="w-4 h-4" />
                        </button>
                      </div>

                      <p className="text-xs text-gray-500 mb-2">{opportunity.companyName}</p>

                      <div className="flex items-center justify-between text-xs">
                        <div className="flex items-center text-green-600 font-medium">
                          <DollarSign className="w-3 h-3 mr-0.5" />
                          {formatCurrency(opportunity.value)}
                        </div>
                        {opportunity.expectedCloseDate && (
                          <div className="flex items-center text-gray-500">
                            <Calendar className="w-3 h-3 mr-1" />
                            {new Date(opportunity.expectedCloseDate).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric'
                            })}
                          </div>
                        )}
                      </div>

                      {opportunity.probability !== undefined && (
                        <div className="mt-2">
                          <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
                            <span>Probability</span>
                            <span>{opportunity.probability}%</span>
                          </div>
                          <div className="w-full h-1.5 bg-gray-200 rounded-full">
                            <div
                              className="h-full bg-primary-500 rounded-full"
                              style={{ width: `${opportunity.probability}%` }}
                            />
                          </div>
                        </div>
                      )}

                      {opportunity.assignedTo && (
                        <div className="mt-2 flex items-center text-xs text-gray-500">
                          <User className="w-3 h-3 mr-1" />
                          {opportunity.assignedTo}
                        </div>
                      )}
                    </div>
                  ))}

                  {stageOpportunities.length === 0 && (
                    <div className="text-center py-8 text-gray-400 text-sm">
                      No opportunities
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default PipelinePage;
